/* eslint-disable no-use-before-define */
/** @flow */

// this file is a light copy of 'app/components/Table/hooks/useFiltersUtils.js'. We can't import useFiltersUtils file in redux middleware, because of circular dependancies, so we use this file instead

import { forEach, isNaN, map } from 'lodash'
import { binaryOperators, gtOperators, ltOperators } from 'app/components/Table/TableView/Elements/Filters/utils.ts'
import { sameFilterMultipleSeparator } from 'app/core/api/http.js'

const PROJECT_HEADER = window.OVM_PROJECT_HEADER

function isAnAttributeOperator(operator) {
  return operator.attr.includes('attributes__') || operator.attr.includes('personAttribute_')
}

function convertOldFiltersFormat(filters) {
  if (filters.find((filter) => filter[0].type === 'bracket')) {
    const _filters = []

    for (let i = 0; i < filters.length; i += 1) {
      const filter = filters[i]

      if (filter[0].type === 'bracket') _filters.push(...filter[0].filters)
      else if (filter[0].type === 'logicOperator' && filter[0].value === '&') continue
      else _filters.push(filter)
    }

    return _filters
  }
  return filters
}

function encodeFilters(str) {
  return encodeURIComponent(str)
}

function concatComplexQueriesToString(queries) {
  return encodeFilters(map(queries, (value, key) => `${key}=${String(value)}`).join('&'))
}

const complexFiltersToQueries = (filters, depth = 0) => {
  let queries = ``
  const headers = {}

  let queriesFilters = []

  const getStringQueries = (_queries, _headers) => {
    forEach(_headers, (header, project) => {
      if (headers[project]) headers[project].push(header)
      else headers[project] = [header]
    })
    return concatComplexQueriesToString(_queries)
  }

  for (let index = 0; index < filters.length; index += 1) {
    const filter = filters[index]

    const isLogicOperator = filter[0].type === 'logicOperator'

    if (isLogicOperator) {
      const { queries: currentQueries, headers: currentHeaders } = filtersToQueries(queriesFilters)
      let filterValue = filter[0].value || '&'

      if (filterValue !== '~') filterValue = `${filterValue} `
      else filterValue = `${filterValue}`

      const str = getStringQueries(currentQueries, currentHeaders)
      queries = `${queries}(${str}) ${filterValue}`

      queriesFilters = []
    } else {
      queriesFilters.push(filter)
    }
  }

  const { queries: currentQueries, headers: currentHeaders } = filtersToQueries(queriesFilters)
  const str = getStringQueries(currentQueries, currentHeaders)
  queries = `${queries}(${str})`

  return { queries, headers }
}

export const filtersToQueries = (filters) => {
  const queries = {}
  const headers = {}

  const isComplexFilter = Boolean(
    filters.find((filter) => {
      if (filter?.[0]?.type === 'logicOperator') return true
      return false
    }),
  )

  if (isComplexFilter) {
    const { queries: complexeQueries, headers: complexeHeaders } = complexFiltersToQueries(
      convertOldFiltersFormat(filters),
    )

    if (complexeHeaders[PROJECT_HEADER] && Array.isArray(complexeHeaders[PROJECT_HEADER])) {
      complexeHeaders[PROJECT_HEADER] = complexeHeaders[PROJECT_HEADER].join(',')
    }

    return { queries: { filters: complexeQueries }, headers: complexeHeaders }
  }

  forEach(filters, (filter) => {
    let operator
    let filterKey
    let multipleValuesQuery = []

    const previousOperators = []

    forEach(filter, (filterPart, index) => {
      if (!filterPart) return
      if (filterPart.type === 'operator') {
        if (multipleValuesQuery.length > 0 && filterKey) {
          queries[filterKey] = JSON.stringify(multipleValuesQuery)
          multipleValuesQuery = []
        }
        if (operator) previousOperators.push(operator)
        operator = filterPart

        filterKey = `${operator.attr}__${operator.serverKey}`.replace('__exact', '')

        if (queries[filterKey]) {
          // $FlowFixMe
          const numberOfSameFilter = Object.keys(queries).filter((key) => key.endsWith(filterKey)).length
          filterKey = `${String(numberOfSameFilter)}${sameFilterMultipleSeparator}${filterKey}`
        }

        if (binaryOperators(operator.value)) {
          queries[filterKey] = operator.formatValue
            ? operator.formatValue(operator.filterValue, operator)
            : operator.filterValue
        }
      } else if (filterPart.type === 'value' && operator) {
        const value = operator?.formatValue?.(filterPart.value, operator) || filterPart.value
        let filterKey = `${operator.attr}__${operator.serverKey}`.replace('__exact', '')

        if (queries[filterKey]) {
          const numberOfSameFilter = Object.keys(queries).filter((key) => key.endsWith(filterKey)).length
          filterKey = `${String(numberOfSameFilter)}${sameFilterMultipleSeparator}${filterKey}`
        }

        const filterStart = queries[filterKey] || ''

        if (['in', 'notIn', 'inList', 'notInList', 'range'].includes(operator.value)) {
          multipleValuesQuery.push(value)
        } else if (
          gtOperators(operator.value) &&
          !isNaN(Number(queries[filterKey])) &&
          operator.inputType !== 'datetime'
        ) {
          queries[filterKey] = Math.max(Number(queries[filterKey]), Number(value))
        } else if (
          ltOperators(operator.value) &&
          !isNaN(Number(queries[filterKey])) &&
          operator.inputType !== 'datetime'
        ) {
          queries[filterKey] = Math.min(Number(queries[filterKey]), Number(value))
        } else {
          queries[filterKey] = `${filterStart ? `${filterStart},` : ''}${String(value)}`
        }

        if (filterPart.projectHeader) {
          if (!headers[PROJECT_HEADER]) headers[PROJECT_HEADER] = []
          if (headers[PROJECT_HEADER].indexOf(filterPart.projectHeader) === -1) {
            headers[PROJECT_HEADER].push(filterPart.projectHeader)
          }
        }

        if (operator.formatMultipleValues && (!filter[index + 1] || filter[index + 1]?.type !== 'value')) {
          queries[filterKey] = operator.formatMultipleValues?.(queries[filterKey], undefined, operator)
        }
      }
    })

    if (multipleValuesQuery.length > 0 && filterKey) {
      // is an attributes__
      if (operator && isAnAttributeOperator(operator)) {
        // is an attributes__ and need to concat
        if (queries[filterKey]) {
          try {
            const parsedQueries = JSON.parse(queries[filterKey])
            if (Array.isArray(parsedQueries)) {
              parsedQueries.push(...multipleValuesQuery)
            }
            queries[filterKey] = JSON.stringify(parsedQueries)
          } catch (error) {
            console.error(error)
          }
          // is an attributes__ and dont need to concat
        } else queries[filterKey] = JSON.stringify(multipleValuesQuery)
        // is not an attributes__ and need to concat
      } else if (queries[filterKey]) queries[filterKey] = `${queries[filterKey]},${multipleValuesQuery.toString()}`
      // is not an attributes__ and dont need to concat
      else queries[filterKey] = multipleValuesQuery.toString()
      multipleValuesQuery = []
    }
  })

  if (headers[PROJECT_HEADER] && Array.isArray(headers[PROJECT_HEADER])) {
    headers[PROJECT_HEADER] = headers[PROJECT_HEADER].join(',')
  }

  return { queries, headers }
}
